import { logger } from '@/utils/logger/logger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { registerAnalyticsAdapter } from '@heidi/runtime/analytics';
import { ambientEvents } from './events/ambient';
import { appearanceEvents } from './events/appearance';
import { audioEvents } from './events/audio';
import { calculatorEvents } from './events/calculators';
import { chatEvents } from './events/chat';
import { chronicleEvents } from './events/chronicle';
import { commsEvents } from './events/comms';
import { contextEvents } from './events/context';
import { diarisationEvents } from './events/diarisation';
import { documentEvents } from './events/documents';
import { evidenceEvents } from './events/evidence';
import { feedbackEvents } from './events/feedback';
import { integrationEvents } from './events/integrations';
import { letterEvents } from './events/letters';
import { noteEvents } from './events/notes';
import { nudgeEvents } from './events/nudges';
import { offlineRecordingEvents } from './events/offline-recording';
import { onboardingEvents } from './events/onboarding';
import { patientEvents } from './events/patients';
import { paymentEvents } from './events/payments';
import { prechartEvents } from './events/prechart';
import { referralEvents } from './events/referrals';
import { scheduleEvents } from './events/schedule';
import { sessionMergeEvents } from './events/session-merge';
import { sessionEvents } from './events/sessions';
import { settingsEvents } from './events/settings';
import { sharingEvents } from './events/sharing';
import { snippetEvents } from './events/snippets';
import { sourceRecommendationEvents } from './events/source-recommendation';
import { taskEvents } from './events/tasks';
import { teamEvents } from './events/teams';
import { templateEvents } from './events/templates';
import { uiEvents } from './events/ui';
import { userEvents } from './events/user';
import { verificationEvents } from './events/verification';
import { workEvents } from './events/work';
import { isSnowplowInitialized } from './index';

export type SnowplowEventHandler = (props: unknown) => void;

export type SnowplowEventMap = Partial<Record<string, SnowplowEventHandler>>;

const eventMap: SnowplowEventMap = {
  ...ambientEvents,
  ...appearanceEvents,
  ...audioEvents,
  ...calculatorEvents,
  ...chatEvents,
  ...commsEvents,
  ...chronicleEvents,
  ...contextEvents,
  ...diarisationEvents,
  ...documentEvents,
  ...evidenceEvents,
  ...feedbackEvents,
  ...integrationEvents,
  ...letterEvents,
  ...noteEvents,
  ...nudgeEvents,
  ...offlineRecordingEvents,
  ...onboardingEvents,
  ...patientEvents,
  ...paymentEvents,
  ...prechartEvents,
  ...referralEvents,
  ...scheduleEvents,
  ...sessionMergeEvents,
  ...sessionEvents,
  ...settingsEvents,
  ...sharingEvents,
  ...snippetEvents,
  ...sourceRecommendationEvents,
  ...taskEvents,
  ...teamEvents,
  ...templateEvents,
  ...uiEvents,
  ...userEvents,
  ...verificationEvents,
  ...workEvents,
};

export function trackSnowplowEvent(eventName: string, props: unknown): void {
  if (!isSnowplowInitialized()) return;

  const handler = eventMap[eventName];
  if (!handler) {
    // Loud in dev/staging so an unmapped event is caught before it ships;
    // silent in prod so a stray event name doesn't spam customer consoles.
    if (getEnvironment() !== ENV_NAME.PROD) {
      logger.warn(`Snowplow event "${eventName}" has no eventMap handler — dropped.`);
    }
    return;
  }

  try {
    handler(props);
  } catch (error) {
    logger.error(`Snowplow ${eventName} tracking failed:`, error);
  }
}

// P2 [3/5]: Snowplow transport binding for the @heidi/runtime/analytics
// pipeline. Deliberately registered on EVERY build INCLUDING on-prem — the
// documented F1 exception (.ai-context/onprem-guardrails.md): on-prem the
// tracker emits same-origin to the in-appliance heidi-collector only. The
// pipeline hands over the RAW caller props (no common enrichment — captured
// legacy behavior, pinned by the parity snapshot); the Iglu schema mapping in
// `eventMap` above stays app-side inside this adapter.
if (typeof window !== 'undefined') {
  registerAnalyticsAdapter({
    destination: 'snowplow',
    enabled: true,
    send: (payload) => {
      trackSnowplowEvent(payload.event, payload.props);
    },
  });
}
